import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Timeline events for Ramayana
const ramayanaEvents = [
  {
    id: '1',
    content: 'Rama, Lakshmana, and Sita leave for exile',
    correctOrder: 1,
    story: 'Rama, the prince of Ayodhya, was exiled to the forest for 14 years due to a promise made by his father King Dasharatha.'
  },
  {
    id: '2',
    content: 'Sita’s abduction by Ravana',
    correctOrder: 2,
    story: 'While in the forest, Sita was abducted by Ravana, the demon king of Lanka, who was enchanted by her beauty.'
  },
  {
    id: '3',
    content: 'Hanuman meets Sita in Lanka',
    correctOrder: 3,
    story: 'Hanuman, the devoted follower of Rama, crossed the ocean to Lanka and found Sita in Ashoka Vatika.'
  },
  {
    id: '4',
    content: 'Rama builds a bridge to Lanka',
    correctOrder: 4,
    story: 'With the help of Hanuman and the vanaras, Rama built a bridge across the ocean to reach Lanka.'
  },
  {
    id: '5',
    content: 'The battle between Rama and Ravana',
    correctOrder: 5,
    story: 'A fierce battle took place between Rama and Ravana, with Rama ultimately defeating the demon king.'
  },
  {
    id: '6',
    content: 'Rama, Sita, and Lakshmana return to Ayodhya',
    correctOrder: 6,
    story: 'After 14 years of exile, Rama, Sita, and Lakshmana returned to Ayodhya where Rama was crowned king.'
  }
];

// Timeline events for Mahabharata
const mahabharataEvents = [
  {
    id: '1',
    content: 'The game of dice - Pandavas lose everything',
    correctOrder: 1,
    story: 'In a game of dice, the Pandavas lost their kingdom, wealth, and even themselves and Draupadi to the Kauravas.'
  },
  {
    id: '2',
    content: 'The Pandavas spend 12 years in exile',
    correctOrder: 2,
    story: 'Following their loss, the Pandavas spent 12 years in exile in the forest as per the conditions of the game.'
  },
  {
    id: '3',
    content: 'The Pandavas spend one year in anonymity',
    correctOrder: 3,
    story: 'After 12 years of exile, the Pandavas spent one more year living in anonymity, hiding their true identities.'
  },
  {
    id: '4',
    content: 'Krishna tries for peace, fails',
    correctOrder: 4,
    story: 'Before the war, Lord Krishna tried to negotiate peace between the Pandavas and Kauravas but was unsuccessful.'
  },
  {
    id: '5',
    content: 'The Kurukshetra war begins',
    correctOrder: 5,
    story: 'The great war of Kurukshetra began between the Pandavas and Kauravas, with Lord Krishna as Arjuna\'s charioteer.'
  },
  {
    id: '6',
    content: 'The end of the war and coronation',
    correctOrder: 6,
    story: 'After 18 days of battle, the Pandavas emerged victorious. Yudhishthira was crowned the king of Hastinapura.'
  }
];

const EpicTimeline = () => {
  const [selectedEpic, setSelectedEpic] = useState('ramayana');
  const [items, setItems] = useState(ramayanaEvents);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);

  // Function to handle epic selection
  const handleEpicChange = (epic) => {
    setSelectedEpic(epic);
    setIsSubmitted(false);
    setShowResult(false);
    setScore(0);
    
    if (epic === 'ramayana') {
      // Shuffle the Ramayana events
      const shuffled = [...ramayanaEvents].sort(() => Math.random() - 0.5);
      setItems(shuffled);
    } else {
      // Shuffle the Mahabharata events
      const shuffled = [...mahabharataEvents].sort(() => Math.random() - 0.5);
      setItems(shuffled);
    }
  };

  // Function to handle drag end
  const onDragEnd = (result) => {
    // Don't do anything if submitted
    if (isSubmitted) return;
    
    // Dropped outside the list
    if (!result.destination) return;

    const reorderedItems = Array.from(items);
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);

    setItems(reorderedItems);
  };

  // Function to check the order
  const checkOrder = () => {
    let correctCount = 0;
    
    items.forEach((item, index) => {
      // Check if the item is in the correct position
      if (item.correctOrder === index + 1) {
        correctCount++;
      }
    });
    
    setScore(correctCount);
    setIsSubmitted(true);
    setShowResult(true);
  };

  // Function to reset the game
  const resetGame = () => {
    setIsSubmitted(false);
    setShowResult(false);
    setScore(0);
    
    if (selectedEpic === 'ramayana') {
      const shuffled = [...ramayanaEvents].sort(() => Math.random() - 0.5);
      setItems(shuffled);
    } else {
      const shuffled = [...mahabharataEvents].sort(() => Math.random() - 0.5);
      setItems(shuffled);
    }
  };

  // Get the current events based on selected epic
  const currentEvents = selectedEpic === 'ramayana' ? ramayanaEvents : mahabharataEvents;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gradient-to-br from-red-50 to-yellow-50 rounded-2xl p-6 md:p-8 shadow-lg">
        <h2 className="text-2xl font-bold text-red-600 mb-6 text-center">Epic Timeline Challenge</h2>
        
        {/* Epic Selection */}
        <div className="flex justify-center mb-6">
          <div className="bg-white rounded-full p-1 shadow-md">
            <button
              onClick={() => handleEpicChange('ramayana')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors ${
                selectedEpic === 'ramayana' 
                  ? 'bg-red-500 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Ramayana
            </button>
            <button
              onClick={() => handleEpicChange('mahabharata')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors ${
                selectedEpic === 'mahabharata' 
                  ? 'bg-red-500 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Mahabharata
            </button>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="bg-white rounded-xl p-4 mb-6 shadow-md">
          <p className="text-gray-700 text-center">
            Arrange the events in the correct chronological order by dragging and dropping them.
          </p>
        </div>
        
        {/* Timeline */}
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="timeline">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-4 mb-8"
              >
                {items.map((item, index) => (
                  <Draggable 
                    key={item.id} 
                    draggableId={item.id} 
                    index={index}
                    isDragDisabled={isSubmitted}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`p-4 rounded-xl shadow-md transition-all duration-200 ${
                          snapshot.isDragging 
                            ? 'bg-red-100 shadow-lg transform rotate-1' 
                            : isSubmitted && item.correctOrder === index + 1
                              ? 'bg-green-100 border-2 border-green-500'
                              : isSubmitted && item.correctOrder !== index + 1
                                ? 'bg-red-100 border-2 border-red-500'
                                : 'bg-white'
                        }`}
                      >
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center mr-4">
                            <span className="text-white font-bold">{index + 1}</span>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{item.content}</p>
                          </div>
                          {isSubmitted && (
                            <div className="ml-4">
                              {item.correctOrder === index + 1 ? (
                                <span className="text-green-500 font-bold">✓</span>
                              ) : (
                                <span className="text-red-500 font-bold">✗</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        
        {/* Submit Button */}
        {!isSubmitted ? (
          <div className="text-center">
            <button
              onClick={checkOrder}
              className="bg-red-600 text-white font-bold py-3 px-8 rounded-full hover:bg-red-700 transition-colors shadow-lg"
            >
              Check Order
            </button>
          </div>
        ) : (
          <div className="text-center">
            <button
              onClick={resetGame}
              className="bg-red-600 text-white font-bold py-3 px-8 rounded-full hover:bg-red-700 transition-colors shadow-lg"
            >
              Try Again
            </button>
          </div>
        )}
        
        {/* Results */}
        {showResult && (
          <div className="mt-6 bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-xl font-bold text-center mb-4 text-red-600">
              {score === items.length 
                ? 'Perfect! You know the epic well! 🏆' 
                : score >= items.length / 2 
                  ? 'Good job! You have a good understanding! 👍' 
                  : 'Keep learning! You\'ll get better! 💪'}
            </h3>
            <div className="text-center text-3xl font-bold mb-4">
              <span className="text-red-600">{score}</span>
              <span className="text-gray-400">/</span>
              <span className="text-gray-600">{items.length}</span>
            </div>
            <p className="text-center text-gray-600 mb-6">
              You got {score} out of {items.length} events in the correct order.
            </p>
            
            {/* Story snippets for incorrect items */}
            <div className="space-y-4">
              {items.map((item, index) => {
                if (item.correctOrder !== index + 1) {
                  return (
                    <div key={item.id} className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
                      <p className="font-medium text-gray-800">{item.content}</p>
                      <p className="text-gray-600 mt-2">{item.story}</p>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        )}
        
        {/* Correct Order Reference */}
        {isSubmitted && (
          <div className="mt-6 bg-blue-50 rounded-xl p-6 shadow-md">
            <h3 className="text-xl font-bold mb-4 text-blue-600">Correct Order:</h3>
            <div className="space-y-3">
              {currentEvents.map((event) => (
                <div key={event.id} className="flex items-center p-3 bg-white rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center mr-4">
                    <span className="text-white font-bold">{event.correctOrder}</span>
                  </div>
                  <p className="font-medium text-gray-800">{event.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EpicTimeline;